const base = process.env.REACT_APP_SERVERURL || "";

export type TypeTagGroup = "BASE_FABRIC" | "WEAVE_TECHNIQUE" | "MOTIF_LAYOUT" | "FINISH";

export type TypeTag = {
  id: string;
  group: TypeTagGroup;
  nameEn: string;
  nameHi?: string | null;
  aliases?: string[];
};

/** What the product and certificate responses carry for each tag. */
export type ProductTypeTag = Pick<TypeTag, "id" | "group" | "nameEn" | "nameHi">;

export const TYPE_TAG_GROUPS: Array<{ group: TypeTagGroup; label: string }> = [
  { group: "BASE_FABRIC", label: "Base fabric" },
  { group: "WEAVE_TECHNIQUE", label: "Weave or technique" },
  { group: "MOTIF_LAYOUT", label: "Motif or layout" },
  { group: "FINISH", label: "Finish" },
];

/** Hindi label when the viewer reads Hindi and the tag has one, else the English name. */
export const typeTagLabel = (tag: Pick<TypeTag, "nameEn" | "nameHi">, language?: string): string =>
  language && language.startsWith("hi") && tag.nameHi ? tag.nameHi : tag.nameEn;

let cache: TypeTag[] | null = null;
let inflight: Promise<TypeTag[] | null> | null = null;
/** Bumped on every reset: an answer that was requested before a change must not refill the cache. */
let generation = 0;

/**
 * The approved pick-list, loaded once per page load. Returns null when the server has tags off
 * (404) or cannot be reached, so the picker simply stays out of the way.
 */
export const loadTypeTags = (): Promise<TypeTag[] | null> => {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  const token = localStorage.getItem("token");
  const mine = generation;
  inflight = fetch(`${base}/type-tags`, {
    headers: token ? { Authorization: "Bearer " + token } : {},
  })
    .then(async (res) => {
      if (!res.ok) return null;
      const json = await res.json().catch(() => null);
      const rows = json?.data;
      if (!json?.success || !Array.isArray(rows)) return null;
      if (mine === generation) cache = rows as TypeTag[];
      return rows as TypeTag[];
    })
    .catch(() => null)
    .finally(() => {
      if (mine === generation) inflight = null;
    });
  return inflight;
};

/** Tests and sign-out: forget what was loaded. */
export const resetTypeTagCache = () => {
  generation += 1;
  cache = null;
  inflight = null;
};

// ── proposals and admin ──

export type TypeTagAdminRow = TypeTag & {
  state: "PENDING" | "APPROVED" | "RETIRED" | "MERGED" | "REJECTED";
  proposedAt?: string | null;
  proposedByOrg?: string | null;
  mergedInto?: string | null;
};

export type TypeTagResult<T = unknown> = {
  ok: boolean;
  status: number;
  message: string;
  data: T | null;
  /** For a duplicate proposal: the tag that already has the name. */
  existingId?: string;
};

/** Plain fetch (not the app-wide helper) so a 403 or 409 comes back as an answer and never redirects. */
async function typeTagRequest<T>(method: "GET" | "POST" | "PUT", path: string, body?: unknown): Promise<TypeTagResult<T>> {
  const token = localStorage.getItem("token");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers.Authorization = "Bearer " + token;
  try {
    const res = await fetch(`${base}/type-tags${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    const ok = res.ok && json?.success !== false;
    return {
      ok,
      status: res.status,
      message: json?.message || (ok ? "" : "Something went wrong. Try again."),
      data: ok ? (json?.data ?? null) : null,
      existingId: json?.data?.existingId,
    };
  } catch {
    return { ok: false, status: 0, message: "Could not reach the server.", data: null };
  }
}

/** The pick-list changes with every proposal decision, so the next picker must reload it. */
const changed = <T,>(result: TypeTagResult<T>): TypeTagResult<T> => {
  if (result.ok) resetTypeTagCache();
  return result;
};

export type TypeTagInput = { group: TypeTagGroup; nameEn: string; nameHi?: string; aliases?: string[] };

export const proposeTypeTag = (input: TypeTagInput) =>
  typeTagRequest<TypeTagAdminRow>("POST", "/proposals", input).then(changed);
export const listPendingTypeTags = () => typeTagRequest<TypeTagAdminRow[]>("GET", "/proposals");
export const approveTypeTag = (id: string, note?: string) =>
  typeTagRequest<TypeTagAdminRow>("POST", `/proposals/${encodeURIComponent(id)}/approve`, { note }).then(changed);
export const rejectTypeTag = (id: string, note?: string) =>
  typeTagRequest<TypeTagAdminRow>("POST", `/proposals/${encodeURIComponent(id)}/reject`, { note }).then(changed);
export const mergeTypeTag = (id: string, targetId: string) =>
  typeTagRequest<TypeTagAdminRow>("POST", `/${encodeURIComponent(id)}/merge`, { targetId }).then(changed);
export const renameTypeTag = (id: string, input: TypeTagInput) =>
  typeTagRequest<TypeTagAdminRow>("PUT", `/${encodeURIComponent(id)}`, input).then(changed);
export type TypeTagSuggestion = TypeTag & { confidence: "EXACT" | "PARTIAL" };

/** Tags the name and sub-category talk about. Applies nothing; the person picks. Empty when tags are off. */
export const suggestTypeTags = async (name: string, subcategoryName?: string): Promise<TypeTagSuggestion[]> => {
  const result = await typeTagRequest<TypeTagSuggestion[]>("POST", "/suggest", { name, subcategoryName });
  return result.ok && Array.isArray(result.data) ? result.data : [];
};

/** Settles the tags of a product the backfill left for review. */
export const confirmTypeTagReview = (productUniqueId: string, typeTagIds: string[]) =>
  typeTagRequest<{ productUniqueId: string }>("POST", `/review/${encodeURIComponent(productUniqueId)}/confirm`, { typeTagIds });

export const retireTypeTag = (id: string) =>
  typeTagRequest<TypeTagAdminRow>("POST", `/${encodeURIComponent(id)}/retire`).then(changed);
